import { createORPCClient, ORPCError, type Client } from "@orpc/client";
import { RPCLink } from "@orpc/client/fetch";

import type { Procedures } from "./generated/procedures.js";
import { err, ok, parseApiError, type ApiError, type NetworkError, type Result } from "./result.js";

export interface ClientConfig {
  /** API base URL — e.g. https://auth.example.com (no trailing slash). */
  baseUrl: string;
  /** Override the global fetch (SSR / tests). */
  fetch?: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  /** Headers merged into every request. */
  headers?: Record<string, string>;
  /** Fired whenever the server returns 401 — typically triggers a re-login flow. */
  onUnauthorized?: () => void;
  /**
   * On a 401 `unauthorized` (the short-lived access cookie lapsed),
   * rotate the pair with the refresh cookie once and retry the call
   * before giving up. Default true.
   */
  autoRefresh?: boolean;
}

/** The raw `@orpc/client` client: one method per API procedure; throws `ORPCError`. */
export type RpcClient = Procedures;

/** Name of an API procedure (its OpenAPI operationId). */
export type ProcedureName = keyof Procedures;

export type ProcedureInput<K extends ProcedureName> =
  Procedures[K] extends Client<any, infer I, any, any> ? I : never;
export type ProcedureOutput<K extends ProcedureName> =
  Procedures[K] extends Client<any, any, infer O, any> ? O : never;

/**
 * "If Input is void, omit the input arg" trick. Inputs that admit
 * `undefined` make the argument optional instead of required.
 */
export type CallArgs<I> = [I] extends [void]
  ? []
  : undefined extends I
    ? [input?: I]
    : [input: I];

export type CallResult<T> = Promise<Result<T, ApiError | NetworkError>>;

/**
 * The SDK client. Every API procedure is a method that never throws:
 *
 *   const um = createClient({ baseUrl: "https://auth.example.com" });
 *   const res = await um.signIn({ email, password });
 *   if (res.ok) { … } else { res.error.code … }
 *
 * Calls go over oRPC (`POST /rpc/<procedure>`); the method names,
 * inputs and outputs are generated from the server's contract. The
 * throwing `@orpc/client` client is available as `um.orpc` for
 * integrations that want it (TanStack Query utils, etc.).
 */
export type UmClient = {
  readonly [K in ProcedureName]: (
    ...args: CallArgs<ProcedureInput<K>>
  ) => CallResult<ProcedureOutput<K>>;
} & {
  readonly orpc: RpcClient;
};

/** Calls whose 401 means the session itself is gone — never refresh-and-retry them. */
const NO_REFRESH = new Set(["refreshToken", "revokeToken", "signOut"]);

const procedureOf = (request: RequestInfo | URL): string => {
  const url = request instanceof Request ? request.url : String(request);
  return new URL(url).pathname.split("/").pop() ?? "";
};

/**
 * A 401 for a missing / expired identity (`unauthorized`), as opposed to
 * a failed credential check (`invalid_email_or_password`, `invalid_code`,
 * …), which a token refresh can't fix and must not be replayed.
 */
const isUnauthorized = async (res: Response): Promise<boolean> => {
  try {
    const body = (await res.clone().json()) as { json?: { code?: unknown } };
    return body.json?.code === "unauthorized";
  } catch {
    return false;
  }
};

const stripTrailing = (b: string): string => b.replace(/\/+$/, "");

/** Map anything a call can throw onto the SDK's error union. */
export const toCallError = (e: unknown): ApiError | NetworkError => {
  if (e instanceof ORPCError) {
    // `data` is the REST error body `{ error, reason?, message?, code? }`.
    return parseApiError(e.status, e.data ?? { error: e.code, message: e.message });
  }
  return {
    kind: "network",
    message: e instanceof Error ? e.message : String(e),
  };
};

/** Await an oRPC call and fold the outcome into a `Result`. */
export const settle = async <T>(call: Promise<T>): CallResult<T> => {
  try {
    return ok(await call);
  } catch (e) {
    return err(toCallError(e));
  }
};

export const createClient = (config: ClientConfig): UmClient => {
  const baseUrl = stripTrailing(config.baseUrl);
  const fetcher = config.fetch ?? ((input, init) => globalThis.fetch(input, init));

  const autoRefresh = config.autoRefresh ?? true;

  // One refresh in flight at a time: a burst of calls that all hit an
  // expired access cookie share a single rotation (refresh tokens are
  // single-use, so parallel rotations would revoke each other).
  let refreshing: Promise<boolean> | null = null;
  const refreshSession = (): Promise<boolean> =>
    (refreshing ??= fetcher(`${baseUrl}/rpc/refreshToken`, {
      method: "POST",
      credentials: "include",
      headers: { ...config.headers, "content-type": "application/json" },
      body: JSON.stringify({ json: {} }),
    })
      .then((res) => res.ok, () => false)
      .finally(() => {
        refreshing = null;
      }));

  const link = new RPCLink({
    url: `${baseUrl}/rpc`,
    headers: () => config.headers ?? {},
    fetch: async (request, init) => {
      const send = (r: RequestInfo | URL) => fetcher(r, { ...init, credentials: "include" });
      const retry =
        autoRefresh && !NO_REFRESH.has(procedureOf(request)) && request instanceof Request
          ? request.clone()
          : undefined;
      let res = await send(request);
      if (res.status === 401 && retry && (await isUnauthorized(res)) && (await refreshSession())) {
        res = await send(retry);
      }
      if (res.status === 401) config.onUnauthorized?.();
      return res;
    },
  });
  const orpc = createORPCClient<RpcClient>(link);

  return new Proxy({} as UmClient, {
    get: (_target, prop) => {
      if (prop === "orpc") return orpc;
      if (typeof prop !== "string" || prop === "then") return undefined;
      const procedure = (orpc as unknown as Record<string, (input?: unknown) => Promise<unknown>>)[prop];
      return (input?: unknown) => settle(procedure!(input));
    },
  });
};
